import React, { useLayoutEffect, useRef } from 'react'
import classNames from 'classnames'
import { isNil } from 'lodash'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import './SegmentedControl.scss'

/**
 * @import import { SegmentedControl } from '@/components/buttons/SegmentedControl'
 * @param {object} props
 * @param {object[]} [props.items] Segments to render, at most one marked active
 * @param {string} props.items[].accessor Unique key for the segment
 * @param {node} [props.items[].label] Visible segment text
 * @param {node} [props.items[].icon] Icon rendered before the label, or alone for icon-only segments
 * @param {node} [props.items[].badge] Count badge rendered after the label
 * @param {object} [props.items[].badgeProps] Extra props for the badge span, e.g. className 'critical'
 * @param {string} [props.items[].ariaLabel] Accessible name, needed for icon-only segments
 * @param {node} [props.items[].tooltip] Tooltip shown on hover
 * @param {boolean} [props.items[].disabled] Blocks selecting the segment
 * @param {boolean} [props.items[].active] Marks the selected segment the thumb slides to
 * @param {Function} [props.items[].onClick] Called when the segment is clicked
 * @param {string} [props.items[].dataTestId] Test id on the segment button
 * @param {string} [props.className] Extra classes on the track element
 * @param {boolean} [props.fullWidth] Stretches segments to fill the container equally
 * @param {boolean} [props.small] Compact height
 */
export const SegmentedControl = ({ items = [], className, fullWidth, small, ...props }) => {
  const trackRef = useRef(null)
  const thumbRef = useRef(null)
  const hasPositionedRef = useRef(false)

  const activeItem = items.find((item) => item.active)
  const activeAccessor = activeItem?.accessor

  // The thumb is positioned imperatively (not through state/JSX styles): its
  // transform/width come from DOM measurements that must land before paint,
  // and routing them through state would add a render per selection change
  // and per resize.
  useLayoutEffect(() => {
    if (isNil(activeAccessor)) {
      hasPositionedRef.current = false
      return undefined
    }

    const positionThumb = () => {
      const thumb = thumbRef.current
      const activeButton = trackRef.current?.querySelector('.segmented-control-segment.active')

      if (isNil(thumb) || isNil(activeButton)) {
        return
      }

      const isFirstPosition = !hasPositionedRef.current

      if (isFirstPosition) {
        thumb.style.transition = 'none'
      }

      thumb.style.transform = `translateX(${activeButton.offsetLeft}px)`
      thumb.style.width = `${activeButton.offsetWidth}px`

      if (isFirstPosition) {
        // Forces a reflow so the initial placement commits without animating,
        // then transitions are handed back to the stylesheet.
        thumb.getBoundingClientRect()
        thumb.style.transition = ''
        hasPositionedRef.current = true
      }
    }

    positionThumb()

    // The track is inline-flex, so any layout shift that happens without a
    // selection change (a badge appearing, fonts loading, a fullWidth resize)
    // changes the track's own size — one observer keeps the thumb aligned.
    const resizeObserver = new ResizeObserver(() => {
      positionThumb()
    })
    resizeObserver.observe(trackRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [activeAccessor, items.length])

  return (
    <div
      ref={trackRef}
      className={classNames('segmented-control', className, { 'full-width': fullWidth, small })}
      {...props}
    >
      {!isNil(activeItem) && (
        <span
          ref={thumbRef}
          className="segmented-control-thumb"
          data-testid="segmented-control-thumb"
          aria-hidden="true"
        />
      )}
      {items.map((item) => {
        const button = (
          <button
            key={item.accessor}
            type="button"
            className={classNames('segmented-control-segment', {
              active: item.active,
              'icon-only': isNil(item.label) && !isNil(item.icon),
              'full-width flex-center': fullWidth,
            })}
            onClick={item.onClick}
            disabled={item.disabled}
            aria-pressed={item.active}
            aria-label={item.ariaLabel}
            data-testid={item.dataTestId}
          >
            {item.icon}
            {item.label}
            {!isNil(item.badge) && (
              // The leading space keeps the accessible name readable ("All 3",
              // not "All3"); flex layout ignores the whitespace-only text node.
              <>
                {' '}
                <span
                  data-testid="tab-count"
                  {...item.badgeProps}
                  className={classNames('tab-count', item.badgeProps?.className)}
                >
                  {item.badge}
                </span>
              </>
            )}
          </button>
        )

        if (!isNil(item.tooltip)) {
          return (
            <InteractiveTooltip key={item.accessor} content={item.tooltip}>
              {button}
            </InteractiveTooltip>
          )
        }

        return button
      })}
    </div>
  )
}
